import { TYPES_VOIE } from './types-voie.js';
export { TYPES_VOIE };
/** Les indices de répétition admis par EDI-TDFC. */
export const INDICES_REPETITION = [
    { code: 'B', libelle: 'bis' },
    { code: 'T', libelle: 'ter' },
    { code: 'Q', libelle: 'quater' },
];
const sansAccents = (s) => s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
/** Le type de voie d'un code (casse ignorée), ou `undefined`. */
export function typeVoieParCode(code) {
    if (!code)
        return undefined;
    const c = code.trim().toUpperCase();
    return TYPES_VOIE.find((t) => t.code === c);
}
/**
 * Un type de voie venu d'ailleurs (annuaire SIRENE, saisie libre) : un code
 * connu (« AV »), ou un libellé (« AVENUE », « avenue ») → son code ; sinon
 * `null`.
 */
export function typeVoieDepuis(valeur) {
    const v = String(valeur ?? '').trim();
    if (!v)
        return null;
    const parCode = typeVoieParCode(v);
    if (parCode)
        return parCode.code;
    const cle = sansAccents(v);
    const parLibelle = [...TYPES_VOIE]
        .filter((t) => sansAccents(t.libelle) === cle)
        .sort((x, y) => Number(Boolean(y.fantoir)) - Number(Boolean(x.fantoir)) || y.code.length - x.code.length)[0];
    return parLibelle?.code ?? null;
}
/** « bis », « B », « b » → « B » ; inconnu → `null`. */
export function normaliserIndice(valeur) {
    const v = sansAccents(String(valeur ?? '').trim());
    if (!v)
        return null;
    const trouve = INDICES_REPETITION.find((i) => i.code.toLowerCase() === v || i.libelle === v);
    return trouve ? trouve.code : null;
}
/**
 * La ligne d'adresse (BT-50) recomposée : « 12 bis avenue des Lilas ».
 * Sans voie, le lieu-dit tient lieu de ligne. Vide si rien n'est saisi.
 */
export function composerAdresse(a, 
/** Libellé d'un code de type de voie : la table en base (14/10/2026), sinon la liste figée. */
libelleTypeVoie) {
    const indice = INDICES_REPETITION.find((i) => i.code === normaliserIndice(a.indice))?.libelle;
    const code = (a.typeVoie ?? '').trim().toUpperCase();
    const type = (code && libelleTypeVoie?.(code)) || typeVoieParCode(code)?.libelle;
    const voie = (a.voie ?? '').trim();
    const morceaux = [(a.numero ?? '').trim(), indice, voie ? type : undefined, voie].filter((m) => Boolean(m));
    const ligne = voie ? morceaux.join(' ') : '';
    return (ligne || (a.lieuDit ?? '').trim()).replace(/\s+/g, ' ').trim();
}
/** Abréviations courantes absentes de la liste des codes. */
const ABREVIATIONS = {
    'av.': 'AV',
    ave: 'AV',
    'bd.': 'BD',
    bld: 'BD',
    'bvd': 'BD',
    'ch.': 'CHE',
    'imp.': 'IMP',
    'pl.': 'PL',
    'rte': 'RTE',
    'r.': 'RUE',
    'all.': 'ALL',
};
/** Libellés, du plus long au plus court, FANTOIR d'abord à libellé égal. */
const PAR_LIBELLE = [...TYPES_VOIE]
    .map((t) => ({ t, cle: sansAccents(t.libelle) }))
    .sort((x, y) => y.cle.length - x.cle.length ||
    Number(Boolean(y.t.fantoir)) - Number(Boolean(x.t.fantoir)) ||
    // « rue » : R et RUE ; le code le plus lisible l'emporte.
    y.t.code.length - x.t.code.length);
/** Types qui désignent un lieu-dit plutôt qu'une voie (zone propre en EDI-TDFC). */
const TYPES_LIEU_DIT = new Set(['LD', 'HAM']);
/**
 * Découpe une ligne d'adresse libre, au mieux : « 12 bis av. des Lilas » →
 * numéro 12, indice B, type AV, voie « des Lilas ». Ce qui n'est pas reconnu
 * reste dans `voie` — jamais perdu, à vérifier par l'utilisateur. Sert à la
 * reprise des adresses déjà saisies et aux produits qui n'envoient qu'une ligne.
 */
export function decouperAdresse(ligne) {
    const brut = String(ligne ?? '')
        .replace(/\s+/g, ' ')
        .trim();
    if (!brut)
        return {};
    const resultat = {};
    let reste = brut;
    const num = /^(\d{1,4})(?:\s*,)?\s*(?:(bis|ter|quater)\b|([btq])(?=[\s,]|$))?[\s,]*(.*)$/i.exec(reste);
    if (num) {
        resultat.numero = num[1];
        const indice = normaliserIndice(num[2] ?? num[3]);
        if (indice)
            resultat.indice = indice;
        reste = num[4] ?? '';
    }
    const minuscule = sansAccents(reste);
    const premier = minuscule.split(' ')[0] ?? '';
    let type;
    let longueur = 0;
    const parAbreviation = ABREVIATIONS[premier];
    if (parAbreviation) {
        type = typeVoieParCode(parAbreviation);
        longueur = premier.length;
    }
    if (!type) {
        const libelle = PAR_LIBELLE.find(({ cle }) => minuscule === cle || minuscule.startsWith(`${cle} `));
        if (libelle) {
            type = libelle.t;
            longueur = libelle.cle.length;
        }
    }
    if (!type) {
        const code = typeVoieParCode(premier.replace(/\.$/, ''));
        // Un code n'est retenu que s'il est suivi d'un nom : « Rue » seul est un nom.
        if (code && minuscule.length > premier.length) {
            type = code;
            longueur = premier.length;
        }
    }
    if (type && minuscule.length > longueur && TYPES_LIEU_DIT.has(type.code) && !resultat.numero) {
        resultat.lieuDit = reste.slice(longueur).trim();
        return resultat;
    }
    if (type && minuscule.length > longueur) {
        resultat.typeVoie = type.code;
        reste = reste.slice(longueur).trim();
    }
    if (reste)
        resultat.voie = reste;
    return resultat;
}
//# sourceMappingURL=adresse.js.map